import { useState } from 'react';
import {
  Box,
  CardContent,
  IconButton,
  List,
  ListItemIcon,
  ListItemText,
  Menu,
  useTheme,
} from '@mui/material';
import {
  ArrowRight,
  Concrete,
  Edit,
  Location,
  MenuIcon,
  BatteryPercentage,
  BananaSensor,
  DeviceIcon,
  SwitchDevice,
  DisableDevice,
  EnableDevice,
} from '../icons';
import {
  StyledCardHeader,
  StyledListItem,
  StyledProjectCard,
} from '../project-card/project-card.styled';
import { t } from 'i18next';
import { routesDetails } from '@/constants';
import { useNavigate } from 'react-router';
import CloseIcon from '@mui/icons-material/Close';
import { StyledMenuItem } from '../pour-card';
import { useUnitType,useCubeOrCylinder, useDateTimeFormat } from '@/hooks';
import { mpaToPsi,cubeToCelinder,deviceNameToDisplayDeviceName } from '@/utils';
import InfoIcon from '@mui/icons-material/Info';
import { useAppStore } from '@/stores/app.store';
import { UserRoleEnum } from '@/models/auth';
import theme from '@/theme/index';
import './sensor-card.css';
import { IPourStatus } from '@/models';

// red, yellow, green based on the percentage
const getBatteryColorPercentage = (percentage) => {
  if (percentage === null || percentage === undefined) {
    return  theme.palette.text.disabled; // Default color if no battery value
  }
  if (percentage < 40) {
    return '#bc544b'; // Red color for battery < 40%
  } else if (percentage < 80) {
    return '#cda984'; // Yellow color for 40% <= battery < 80%
  } else {
    return '#009999'; // Green color for battery >= 80%
  }
};
interface IDeviceCardProps {
  testid: string;
  pourStatus: IPourStatus;
  isDisabled: boolean;
  pourId: string;
  deviceName: string;
  location: string;
  sensor_id: string;
  banana_id:string;
  handleDeleteSensorClick: (sensorId: string) => void;
  handleToggleEnableSensorClick: (sensorId: string, isDisabled: boolean) => void;
  projectId: string;
  errors?: any[]; // Add errors as a prop
  device_battery: any;
  last_strength: any;
  castStartTime: Date;
}

const SensorCard = ({
  testid,
  pourId,
  pourStatus,
  isDisabled,
  projectId,
  deviceName,
  location,
  sensor_id,
  banana_id,
  device_battery,
  last_strength,
  castStartTime: cast_start_time,
  handleDeleteSensorClick,
  handleToggleEnableSensorClick,
  errors = [], // Default to an empty array if no errors are passed
}: IDeviceCardProps) => {
  const { currentUser } = useAppStore();
  const theme = useTheme();
  const { isMetric } = useUnitType();
  const { formatDate } = useDateTimeFormat();
  const { isCylinder } = useCubeOrCylinder();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const batteryValueSymbol = device_battery == null ? '∅' : `${device_battery}%`;
  const batteryColor = (device_battery == null || isDisabled) ?  theme.palette.text.disabled : getBatteryColorPercentage(device_battery);
  const disabledColor = theme.palette.text.disabled;
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (e: any) => {
    e?.stopPropagation?.();
    setAnchorEl(null);
  };

  // Filter errors related to the current sensor
  const sensorErrors = errors.filter(({element_id}) => element_id === sensor_id)
  
  return (
    <>
      <Box sx={{ overflow: 'auto', flex: 1 }} data-testid={testid}>
        <StyledProjectCard
          onClick={() =>
            navigate(
              routesDetails.Sensor(
                projectId,
                pourId,
                sensor_id,
                deviceName,
                pourStatus
              ),
              {
                state: { errors }, // Pass errors through state
              }
            )
          }
        >
          <StyledCardHeader
            sx={{ 
              // If disabled, use disabledColor. Otherwise, let it fall back default (primary.darker)
              backgroundColor: isDisabled ? disabledColor : undefined 
            }}
            action={
              <IconButton aria-label='view Project'>
                <ArrowRight />
              </IconButton>
            }
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>{deviceNameToDisplayDeviceName(deviceName)}</span>
                {device_battery !== null && (
                  <BatteryPercentage
                    value={device_battery}
                    fillColor={batteryColor}
                    batterySymbol={batteryValueSymbol}
                    strokeColor="white"
                  />
                )}
              </div>
            }
          />

          <CardContent sx={{ position: 'relative' }}>
            <Box
              sx={{
                position: 'absolute',
                top: '0',
                right: '0',
                zIndex: '99',
              }}
            >
              {currentUser?.user?.role !== UserRoleEnum.customerMngr && <> 
              <IconButton
                aria-controls={open ? 'basic-menu' : undefined}
                aria-haspopup='true'
                aria-expanded={open ? 'true' : undefined}
                onClick={handleClick}
                aria-label='options'
              >
              <MenuIcon/>
              </IconButton>
              <Menu
                id='basic-menu'
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                  'aria-labelledby': 'basic-button',
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                
                <StyledMenuItem
                  onClick={e => {
                    e.stopPropagation();
                    navigate(routesDetails.editSensor(sensor_id,projectId, pourId,pourStatus));
                    handleClose(e);
                  }}
                >
                  <span>
                    <Edit />
                  </span>
                  {t('editSensor')}
                </StyledMenuItem>

                <StyledMenuItem
                  onClick={e => {
                    e.stopPropagation();
                    navigate(routesDetails.UpsertDevice(projectId, pourId, pourStatus ,sensor_id, banana_id, true));
                    handleClose(e);
                  }}
                >
                  <span>
                    <SwitchDevice />
                  </span>
                  {t('switchDevice')}
                </StyledMenuItem>

                {!isDisabled && 
                <StyledMenuItem
                  disabled={pourStatus === 'finished'}
                  onClick={e => {
                    e.stopPropagation();
                    handleToggleEnableSensorClick(sensor_id,false);
                    handleClose(e);
                  }}
                >
                  <Box
                    component='span'
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      '& svg': {
                        width: '15px',
                        height: '15px',
                        fill: 'red',
                      },
                    }}
                  >
                  <DisableDevice />
                  </Box>
                  {t('disableSensor')}
                </StyledMenuItem> }

                {isDisabled && <StyledMenuItem
                  disabled={pourStatus === 'finished'}
                  onClick={e => {
                    e.stopPropagation();
                    handleToggleEnableSensorClick(sensor_id,true);
                    handleClose(e);
                  }}
                >
                  <Box
                    component='span'
                    className="icon-container"
                  >
                  <EnableDevice />
                  </Box>
                  {t('enableSensor')}
                </StyledMenuItem> }

                {cast_start_time === null &&<StyledMenuItem
                  disabled={pourStatus === 'finished'}
                  onClick={e => {
                    e.stopPropagation();
                    handleDeleteSensorClick(sensor_id);
                    handleClose(e);
                  }}
                >
                  <Box
                    component='span'
                    className="icon-container"
                  >
                    <CloseIcon />
                  </Box>
                  {t('delete')}
                </StyledMenuItem>}
              </Menu></>}
            </Box>

            <List dense disablePadding>
              <StyledListItem>
                <ListItemIcon>
                  <Location />
                </ListItemIcon>
                <ListItemText>
                  <span>{`${t('location')} :`}</span>
                  {location}
                </ListItemText>
              </StyledListItem>
              <StyledListItem>
                <ListItemIcon
                  sx={{
                    '& path': {
                      fill: theme.palette.primary.main,
                      fillOpacity: 1,
                    },
                  }}
                >
                  <DeviceIcon width="22px" height="18px" />
                </ListItemIcon>
                <ListItemText>
                  <span>{`${t('castTime')} :`}</span>
                  {cast_start_time ? formatDate(cast_start_time, "time") : t('notStarted') }
                </ListItemText>
              </StyledListItem>
              {banana_id && (
              <StyledListItem>
                <ListItemIcon
                  sx={{
                    '& path': {
                      fill: theme.palette.primary.main,
                      fillOpacity: 1,
                    },
                  }}
                >
                <BananaSensor width="20px" height="32px" />
                </ListItemIcon>

                  <ListItemText>
                    <span>{`${t('bananaId')} :`}</span>
                    {banana_id}
                  </ListItemText>

              </StyledListItem>
              )}
              <StyledListItem>
                <ListItemIcon
                  sx={{
                    '& path': {
                      fill: theme.palette.primary.main,
                      fillOpacity: 1,
                    },
                  }}
                >
                  <Concrete />
                </ListItemIcon>
                <ListItemText>
                  <span>{`${t('lastStrength')} :`}</span>
                  {last_strength?
                  isMetric?
                       isCylinder?
                        `${cubeToCelinder(last_strength)?.toFixed(2)} ${t('mpa')}` 
                        : `${last_strength?.toFixed(2)} ${t('mpa')}` 
                  :isCylinder?
                       `${mpaToPsi(cubeToCelinder(last_strength))?.toFixed(0)} ${t('psi')}` 
                  :`${mpaToPsi(last_strength)?.toFixed(0)} ${t('psi')}`
                  : `${t('NA')}`}
                </ListItemText>
              </StyledListItem>
              <StyledListItem>
                <ListItemIcon>
                  {sensorErrors.length > 0 && <InfoIcon sx={{ color: 'red' }} />}
                </ListItemIcon>
                <ListItemText>
                  {sensorErrors.length > 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        overflow: 'visible',
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        maxWidth: '100%',
                      }}
                    >
                      {sensorErrors.map((error, index, array) => {
                          const message = error.event_type.includes("didn't broadcast more than 6 hours")
                            ? "didn't broadcast more than 6 hours"
                            : error.event_type.replace(/^Device \d+ /, "");

                          const isLast = index === array.length - 1;

                          return (
                            <span key={index}>
                              {message}
                              {!isLast && ", "}
                            </span>
                          );
                      })}
                    </Box>
                  )}
                </ListItemText>
              </StyledListItem>
            </List>
          </CardContent>
        </StyledProjectCard>
      </Box>
    </>
  );
};

export default SensorCard;
